import {
  Box,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CustomAvatar } from "../../../components/custom-avatar";
import { getLogo } from "../../../utils/getLogo";
import { CompanyHistory, ContactHistory, EditCompanyData, EditCompanyDataCompany, MemberData } from "../../types";
import { CompanyContext } from "../CompanyContext";
import StickyTable from "../../../shared/StickyTable";
import { formatDate } from "../../../../utils";
import useResponsive from "../../../../minimals/hooks/useResponsive";
import Iconify from "../../../../minimals/components/iconify";
import { IconButton } from "@mui/material";

// Matching the contacts list, so a phone or email action looks the same everywhere.
const IOS_GREEN = "#34C759";
const IOS_BLUE = "#007AFF";

const memberActionSx = (color: string) => ({
  width: 36,
  height: 36,
  color: "#fff",
  backgroundColor: color,
  "&:hover": { backgroundColor: color, filter: "brightness(0.92)" }
});

interface IProps {
  searchKeywords?: string;
}

export default function MembersTable(props: IProps) {
  const { searchKeywords } = props;
  const { company } = useContext(CompanyContext);
  const searchKeywordParts = searchKeywords?.trim().split(" ");
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");
  const [members, setMembers] = useState<MemberData[]>([]);
  // Seven columns can't work on a phone; mobile renders the same rows as cards.
  const isMobile = useResponsive("down", "md");

  const getAndSetMembers = async () => {
    if (!id) return;
    const { data: members } = await axios.get(
      `/api/clientphonebook/companies/getContacts?id=${id}&onlyShowActive=true`
    );
    const contactIds = members.map((m: MemberData) => m.contactId);
    const { data: allHistory } = await axios.post(`/api/clientphonebook/contactHistory/getByContactIds`, contactIds);
    const membersWithHistory = members.map((m: MemberData) => {
      const historyForThisMember = allHistory.find((h: any) => h.contactId === m.contactId);
      return {
        ...m,
        createdDate: historyForThisMember?.date || null
      };
    });
    setMembers(membersWithHistory);
  };

  useEffect(() => {
    getAndSetMembers();
  }, []);

  const updatePrimaryContact = async (primaryContactId: number | null) => {
    await axios.put(`/api/clientphonebook/companies/updatePrimaryContact`, {
      companyId: company?.id,
      primaryContactId
    });
    getAndSetMembers();
  };

  const filteredData = members.filter((m) => {
    if (!searchKeywordParts || searchKeywordParts.length === 0) return true;
    return searchKeywordParts.some((p) => m.name.toLowerCase().includes(p.toLowerCase()));
  });
  const formatPhoneNumber = (phone: string | undefined) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phone;
  };

  if (isMobile) {
    return (
      <Box>
        {filteredData.map((m) => {
          const businessPhone = m?.phones?.find((p) => p.type === "Business")?.number;
          const cityLine = [m.location.city, [m.location.state, m.location.zip].filter(Boolean).join(" ")]
            .filter(Boolean)
            .join(", ");
          const address = [m.location.address1, m.location.address2].filter(Boolean).join(" ");
          return (
            <Box
              key={m.contactId}
              sx={{
                display: "flex",
                gap: 1.5,
                px: 2,
                py: 1.75,
                borderBottom: "1px solid",
                borderColor: "divider",
                // Same deactivated treatment the table uses.
                opacity: m.isContactActive ? 1 : 0.65,
                borderLeft: m.isContactActive ? "4px solid transparent" : "4px solid #ff000040"
              }}
            >
              <Link
                to={`../phonebook/contact/profile?id=${m.contactId}`}
                style={{ textDecoration: "none", color: "#212B36", display: "flex", gap: 12, flex: 1, minWidth: 0 }}
              >
                <CustomAvatar
                  alt={`${m.name}`}
                  name={`${m.name}`}
                  src={getLogo(m.role && m.email?.split("@")[1])}
                  sx={{ width: 36, height: 36 }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="subtitle2">{m.name}</Typography>
                  {m.role && (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {m.role}
                    </Typography>
                  )}
                  {(address || cityLine) && (
                    <Typography variant="caption" sx={{ color: "text.disabled", display: "block" }}>
                      {[address, cityLine].filter(Boolean).join(", ")}
                    </Typography>
                  )}
                </Box>
              </Link>

              {/* Outside the profile link so these act on the number/address. The star
                  replaces the table's Primary Contact switch, which is too wide here. */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
                <Box sx={{ width: 36, flexShrink: 0 }}>
                  {m.email ? (
                    <IconButton aria-label={`Email ${m.name}`} href={`mailto:${m.email}`} sx={memberActionSx(IOS_BLUE)}>
                      <Iconify icon="eva:email-fill" width={19} />
                    </IconButton>
                  ) : null}
                </Box>
                <Box sx={{ width: 36, flexShrink: 0 }}>
                  {businessPhone ? (
                    <IconButton
                      aria-label={`Call ${m.name}`}
                      href={`tel:${businessPhone}`}
                      sx={memberActionSx(IOS_GREEN)}
                    >
                      <Iconify icon="eva:phone-fill" width={19} />
                    </IconButton>
                  ) : null}
                </Box>
                <IconButton
                  size="small"
                  aria-label={
                    m.isPrimaryContact ? `${m.name} is the primary contact` : `Make ${m.name} primary contact`
                  }
                  disabled={!company?.isActive}
                  onClick={() => updatePrimaryContact(m.isPrimaryContact ? null : m.contactId)}
                  sx={{ flexShrink: 0, color: m.isPrimaryContact ? "warning.main" : "text.disabled" }}
                >
                  <Iconify icon={m.isPrimaryContact ? "eva:star-fill" : "eva:star-outline"} width={20} />
                </IconButton>
              </Stack>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <StickyTable>
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
          <TableCell>Role</TableCell>
          <TableCell>Location</TableCell>
          <TableCell>Phone</TableCell>
          <TableCell>Email</TableCell>
          <TableCell>Date Created</TableCell>
          <TableCell align="right">Primary Contact</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {filteredData.map((m) => (
          <Tooltip
            key={m.contactId}
            title={m.isContactActive ? null : "Contact is deactivated"}
            placement="top"
            followCursor
          >
            <TableRow key={m.contactId} sx={{ opacity: m.isContactActive ? 1 : 0.65 }}>
              <TableCell>
                <Box
                  paddingLeft={0.5}
                  sx={{
                    borderLeft: !m.isContactActive ? "4px solid #ff000040" : "4px solid transparent"
                  }}
                >
                  <Typography variant="subtitle2">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CustomAvatar
                        alt={`${m.name}`}
                        name={`${m.name}`}
                        src={getLogo(m.role && m.email?.split("@")[1])}
                      />
                      <Box>
                        <Typography variant="subtitle2">
                          <Link
                            to={`../phonebook/contact/profile?id=${m.contactId}`}
                            style={{
                              textDecoration: "none",
                              color: "#212B36"
                            }}
                          >
                            {m.name}
                          </Link>
                        </Typography>
                      </Box>
                    </Stack>
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{m?.role}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {m.location.address1} {m.location.address2}
                </Typography>
                <Typography variant="body2">
                  {m.location.city || ""}
                  {m.location.city && m.location.state && ","} {m.location.state || ""} {m.location.zip || ""}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatPhoneNumber(m?.phones?.find((p) => p.type === "Business")?.number)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{m?.email}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{formatDate(m.createdDate) || ""}</Typography>
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="right">
                  <Switch
                    color="success"
                    checked={m.isPrimaryContact}
                    onChange={(e, v) => updatePrimaryContact(v ? m.contactId : null)}
                    disabled={!company?.isActive}
                  />
                </Stack>
              </TableCell>
            </TableRow>
          </Tooltip>
        ))}
      </TableBody>
    </StickyTable>
  );
}
